import React, { useState, useMemo } from 'react';
import BreadCrumb from '../../Components/Common/BreadCrumb';
import {
  Card,
  CardBody,
  Col,
  Container,
  CardHeader,
  Row,
  Input,
  Label,
  FormFeedback,
  Form,
  Button,
  ButtonGroup,
} from 'reactstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getCategoriesQuery, getProductByIdQuery } from '../../queries';
import {
  createProduct,
  editProduct,
  uploadProductImage,
  updateProductImage,
} from '../../api/products';
import { toast, ToastContainer } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import settings from '../../settings/settings';

const unitOptions = [
  { label: 'Piece', value: 'piece' },
  { label: 'Kilogram', value: 'kilogram' },
  { label: 'Meter', value: 'meter' },
  { label: 'Liter', value: 'liter' },
];

const ProductAdd = (props) => {
  const history = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language === "rs" ? "ru" : i18n.language;
  document.title = (isEdit ? t('Edit Product') : t('Create Product')) + ' | Bozor Admin';
  const queryClient = useQueryClient();
  const [selectedImage, setSelectedImage] = useState(null);

  const { data: categoriesData } = useQuery({
    ...getCategoriesQuery({ isAll: true }),
  });

  const { data: productData, isLoading: isProductLoading } = useQuery({
    ...getProductByIdQuery(id),
    enabled: isEdit,
  });

  const product = productData?.data?.data;

  const categories = useMemo(() => {
    return categoriesData?.data?.data || [];
  }, [categoriesData]);

  // Helper to process categories into tree (similar to Categories.js)
  const processedCategories = useMemo(() => {
    if (!categories.length) return [];

    // 1. Create a map of items
    const map = {};
    const cats = categories.map((c) => ({ ...c, children: [] }));
    cats.forEach((c) => (map[c.id] = c));

    // 2. Build tree structure
    const roots = [];
    cats.forEach((c) => {
      if (c.upperId && map[c.upperId]) {
        map[c.upperId].children.push(c);
      } else {
        roots.push(c);
      }
    });

    // 3. Flatten for dropdown display
    const flatten = (nodes, depth = 0) => {
      let result = [];
      nodes.forEach((node) => {
        const nodeWithDepth = { ...node, depth };
        result.push(nodeWithDepth);
        if (node.children && node.children.length > 0) {
          result = result.concat(flatten(node.children, depth + 1));
        }
      });
      return result;
    };

    return flatten(roots);
  }, [categories]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      categoryId: product?.categoryId || '',
      names: [
        { localeKey: 'ru', name: product?.locales?.find((l) => l.localeKey === 'ru')?.name || '' },
        { localeKey: 'uz', name: product?.locales?.find((l) => l.localeKey === 'uz')?.name || '' },
        {
          localeKey: 'uzc',
          name: product?.locales?.find((l) => l.localeKey === 'uzc')?.name || '',
        },
      ],
      unitOfMeasure: product?.unitOfMeasure?.toLowerCase() || 'piece',
      productCode: product?.productCode || '',
      packageCode: product?.packageCode || '',
      barcode: product?.barcode || '',
    },
    validationSchema: Yup.object({
      categoryId: Yup.string().required(t('Please select a category')),
      names: Yup.array().of(
        Yup.object().shape({
          name: Yup.string().when('localeKey', {
            is: 'ru',
            then: () => Yup.string().required(t('Name (RU) is required')),
            otherwise: () => Yup.string(),
          }),
        }),
      ),
      unitOfMeasure: Yup.string().required(t('Unit of measure is required')),
      barcode: Yup.string().required(t('Barcode is required')),
    }),
    onSubmit: (values) => {
      if (isEdit && !validation.dirty && !selectedImage) {
        toast.info(t('No changes detected'));
        history('/products');
        return;
      }

      // Filter out empty names if needed, or send all
      // The API expects locales array
      const payload = {
        categoryId: values.categoryId,
        locales: values.names,
        unitOfMeasure: values.unitOfMeasure,
        productCode: values.productCode,
        packageCode: values.packageCode,
        barcode: values.barcode,
      };

      if (isEdit) {
        if (!validation.dirty && selectedImage) {
          const formData = new FormData();
          const hasImage = !!product?.imageId;
          formData.append('id', hasImage ? product.imageId : id);
          formData.append('image', selectedImage);

          const uploadFn = hasImage ? updateProductImage : uploadProductImage;

          uploadFn(formData)
            .then(() => {
              queryClient.invalidateQueries({ queryKey: ['products'] });
              queryClient.invalidateQueries({ queryKey: ['product', id] });
              toast.success(t('Product image updated successfully'));
              history('/products');
            })
            .catch((err) => {
              toast.error(err?.response?.data?.error?.message || t('Error updating product image'));
            });
          return;
        }

        editProduct({ ...payload, id })
          .then(async () => {
            if (selectedImage) {
              try {
                const formData = new FormData();
                const hasImage = !!product?.imageId;
                formData.append('id', hasImage ? product.imageId : id);
                formData.append('image', selectedImage);
                const uploadFn = hasImage ? updateProductImage : uploadProductImage;
                await uploadFn(formData);
              } catch (imgErr) {
                console.error('Image upload failed', imgErr);
                toast.warning(t('Product updated, but image upload failed'));
              }
            }
            queryClient.invalidateQueries({ queryKey: ['products'] });
            queryClient.invalidateQueries({ queryKey: ['product', id] });
            toast.success(t('Product updated successfully'));
            history('/products');
          })
          .catch((err) => {
            toast.error(err?.response?.data?.error?.message || t('Error updating product'));
            console.error(err);
          });
      } else {
        createProduct(payload)
          .then(async (res) => {
            const productId = res?.data?.data?.id || res?.data?.id; // Handle likely response wrappers

            if (productId && selectedImage) {
              try {
                const formData = new FormData();
                formData.append('id', productId);
                formData.append('image', selectedImage);
                await uploadProductImage(formData);
              } catch (imgErr) {
                console.error('Image upload failed', imgErr);
                toast.warning(t('Product created, but image upload failed'));
                // We still redirect even if image fails, but warn user
              }
            }

            queryClient.invalidateQueries({ queryKey: ['products'] });
            toast.success(t('Product created successfully'));
            history('/products');
          })
          .catch((err) => {
            toast.error(err?.response?.data?.error?.message || t('Error creating product'));
            console.error(err);
          });
      }
    },
  });

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={isEdit ? t('Edit Product') : t('Create Product')}
          pageTitle="Ecommerce"
        />
        <ToastContainer closeButton={false} limit={1} />

        <Form
          onSubmit={(e) => {
            e.preventDefault();
            validation.handleSubmit();
          }}
        >
          <Row>
            {/* Left Column (same as before) */}
            <Col lg={8}>
              {/* ... (Cards are same) ... */}
              <Card>
                <CardHeader>
                  <h5 className="card-title mb-0">{t('General Information')}</h5>
                </CardHeader>
                <CardBody>
                  {/* ... (Existing fields) ... */}
                  <div className="mb-3">
                    <Label className="form-label" htmlFor="category-select">
                      {t('Category')}
                    </Label>
                    <Input
                      type="select"
                      className="form-select"
                      id="category-select"
                      name="categoryId"
                      value={validation.values.categoryId}
                      onBlur={validation.handleBlur}
                      onChange={validation.handleChange}
                      invalid={
                        validation.errors.categoryId && validation.touched.categoryId ? true : false
                      }
                    >
                      <option value="">{t('Select Category')}</option>
                      {processedCategories.map((item) => (
                        <option value={item.id} key={item.id}>
                          {'\u00A0'.repeat((item.depth || 0) * 4) +
                            (item.locales?.find((l) => l.localeKey === currentLang)?.name ||
                              item.locales?.[0]?.name ||
                              t('Unknown'))}
                        </option>
                      ))}
                    </Input>
                    {validation.errors.categoryId && validation.touched.categoryId ? (
                      <FormFeedback type="invalid">{validation.errors.categoryId}</FormFeedback>
                    ) : null}
                  </div>

                  <Row>
                    <Col md={6}>
                      <div className="mb-3">
                        <Label className="form-label" htmlFor="barcode-input">
                          {t('Barcode')}
                        </Label>
                        <Input
                          type="text"
                          className="form-control"
                          id="barcode-input"
                          name="barcode"
                          placeholder={t('Enter barcode')}
                          value={validation.values.barcode}
                          onBlur={validation.handleBlur}
                          onChange={validation.handleChange}
                          invalid={
                            validation.errors.barcode && validation.touched.barcode ? true : false
                          }
                        />
                        {validation.errors.barcode && validation.touched.barcode ? (
                          <FormFeedback type="invalid">{validation.errors.barcode}</FormFeedback>
                        ) : null}
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-3">
                        <Label className="form-label" htmlFor="unit-input">
                          {t('Unit of Measure')}
                        </Label>
                        <div>
                          <ButtonGroup>
                            {unitOptions.map((option) => (
                              <Button
                                key={option.value}
                                color={
                                  validation.values.unitOfMeasure === option.value
                                    ? 'primary'
                                    : 'light'
                                }
                                onClick={() =>
                                  validation.setFieldValue('unitOfMeasure', option.value)
                                }
                                active={validation.values.unitOfMeasure === option.value}
                                type="button"
                              >
                                {t(option.label)}
                              </Button>
                            ))}
                          </ButtonGroup>
                        </div>
                        {validation.errors.unitOfMeasure && validation.touched.unitOfMeasure ? (
                          <div className="invalid-feedback d-block">
                            {validation.errors.unitOfMeasure}
                          </div>
                        ) : null}
                      </div>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <div className="mb-3">
                        <Label className="form-label" htmlFor="productCode-input">
                          {t('Product Code')}
                        </Label>
                        <Input
                          type="text"
                          className="form-control"
                          id="productCode-input"
                          name="productCode"
                          placeholder={t('Enter product code')}
                          value={validation.values.productCode}
                          onBlur={validation.handleBlur}
                          onChange={validation.handleChange}
                        />
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-3">
                        <Label className="form-label" htmlFor="packageCode-input">
                          {t('Package Code')}
                        </Label>
                        <Input
                          type="text"
                          className="form-control"
                          id="packageCode-input"
                          name="packageCode"
                          placeholder={t('Enter package code')}
                          value={validation.values.packageCode}
                          onBlur={validation.handleBlur}
                          onChange={validation.handleChange}
                        />
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>

              <Card>
                <CardHeader>
                  <h5 className="card-title mb-0">{t('Localized Names')}</h5>
                </CardHeader>
                <CardBody>
                  {validation.values.names.map((locale, index) => (
                    <div className="mb-3" key={index}>
                      <Label className="form-label">
                        {t('Name')} ({locale.localeKey.toUpperCase()})
                      </Label>
                      <Input
                        type="text"
                        className="form-control"
                        name={`names[${index}].name`}
                        placeholder={`${t('Enter name in')} ${locale.localeKey}`}
                        value={validation.values.names[index].name}
                        onBlur={validation.handleBlur}
                        onChange={validation.handleChange}
                        // Simplistic error handling for nested array
                        invalid={
                          validation.errors.names?.[index]?.name &&
                          validation.touched.names?.[index]?.name &&
                          locale.localeKey === 'ru'
                        }
                      />
                      {validation.errors.names?.[index]?.name &&
                        validation.touched.names?.[index]?.name &&
                        locale.localeKey === 'ru' && (
                          <div className="invalid-feedback d-block">
                            {validation.errors.names[index].name}
                          </div>
                        )}
                    </div>
                  ))}
                </CardBody>
              </Card>

              <div className="text-end mb-3">
                <Button type="submit" color="success" className="w-sm">
                  {isEdit ? t('Update Product') : t('Create Product')}
                </Button>
              </div>
            </Col>

            <Col lg={4}>
              <Card>
                <CardHeader>
                  <h5 className="card-title mb-0">{t('Product Image')}</h5>
                </CardHeader>
                <CardBody>
                  <div className="text-center">
                    {(selectedImage || (isEdit && product?.imageId)) && (
                      <div className="mb-3">
                        <img
                          src={
                            selectedImage
                              ? URL.createObjectURL(selectedImage)
                              : `${settings.catalogBaseURL}/products/images/${product.imageId}`
                          }
                          alt="Product"
                          className="img-thumbnail"
                          style={{ maxHeight: '200px' }}
                        />
                      </div>
                    )}
                    <Input
                      type="file"
                      onChange={(e) => {
                        if (e.target.files && e.target.files[0]) {
                          const file = e.target.files[0];
                          if (['image/jpeg', 'image/png'].includes(file.type)) {
                            setSelectedImage(file);
                          } else {
                            toast.error(t('Only PNG and JPG images are allowed'));
                            e.target.value = null; // Reset input
                          }
                        }
                      }}
                      accept=".png, .jpg, .jpeg"
                    />
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Form>
      </Container>
    </div>
  );
};

export default ProductAdd;
